import { ReactElement, useEffect, useRef, useState, ReactFragment } from 'react'
import { DateCarousel, Heading, Text } from '@raskrask-dk/rr-components'
import { useTranslation } from 'react-i18next'
import { PageTemplate } from '../chemistry/skeleton'
import { ItemWithHeader } from '../chemistry/ItemWithHeader'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { getIsMobile } from '../../redux/reducers/appSettings'
import { CalendarTemplateProps } from './props'
import {
    getActiveTab,
    getIsServicePickerOpen,
    setIsServicePickerOpen,
} from '../../redux/reducers/calendarSettings'
import { ServicePicker, ServiceTexts } from './styles'
import { RenderIf } from '../chemistry/higherOrderComponents/renderIf'
import {
    GoalComponent,
    ServiceTypesComponent,
    KlippeCardButton,
    GoToCalendarComponent,
    GoToServicePicker,
    PartnerPickerComponent,
    ServicePickerComponent,
} from '../chemistry/calendarComponents/components'
import { PartnerModalComponent } from '../chemistry/calendarComponents'
import { GoalBF, ServiceBF } from '../../models'

export const CalendarTemplate = ({
    basisType,
    goalHandler,
    serviceHandler,
    partnerHandler,
    dateHandler,
    timeHandler,
    modalHandler,
    onClickBook,
    canClickNext,
    onClickKlippeKortButtonHeaderDesktop,
    isCompanyBooking,
}: CalendarTemplateProps): ReactElement => {
    const { t, i18n } = useTranslation()
    const dispatch = useAppDispatch()
    const servicePickerRef = useRef<HTMLDivElement>(null)
    const [handleOnCloseModal, handleOnOpenModal, isModalOpen] = modalHandler
    const [selectedGoal, setGoal, goals] = goalHandler
    const [selectedService, setService, services] = serviceHandler
    const [selectedPartner, setPartner, partners] = partnerHandler
    const [
        selectedDate,
        setDate,
        dates,
        onScrollEvent,
        carouselRef,
        partnerModalCarouselRef,
        onClickDate,
    ] = dateHandler
    const [time, timeSlots, setTime] = timeHandler

    const basisTypeId = basisType.id

    const activeTab = useAppSelector(getActiveTab)
    const isMobile = useAppSelector(getIsMobile)
    const isServiceTypePickerOpen = useAppSelector(getIsServicePickerOpen)

    const [showCTAHelpText, setCTAHelpText] = useState(false)

    const carouselLocale = i18n.language === 'en-GB' ? 'en' : 'da' // da-DK === "da"

    // minHeight of servicePicker - for CSS purposes
    const [servicePickerMinHeight, setServicePickerMinHeight] = useState('0')

    // Sets height of ServicePicker (implicit only on isMobile)
    useEffect(() => {
        if (servicePickerRef.current)
            setServicePickerMinHeight(
                isServiceTypePickerOpen
                    ? `calc(100vh - ${servicePickerRef.current.offsetTop}px - 32px)`
                    : '0'
            )
    }, [isServiceTypePickerOpen])

    const onClickServiceCTAMobile = () => {
        dispatch(setIsServicePickerOpen(false))
    }
    const onClickGoToServicePicker = () => {
        dispatch(setIsServicePickerOpen(true))
    }

    const carouselComponent = () => (
        <DateCarousel
            dates={dates}
            datesToShow={isMobile ? 5 : 12}
            onClickDate={onClickDate}
            onScrollEvent={onScrollEvent}
            carouselLocale={carouselLocale}
            ref={carouselRef}
        />
    )

    const goalsComponent: ReactFragment = GoalComponent(
        goals,
        selectedGoal as GoalBF,
        (goal) => {
            if (isMobile && selectedGoal?.id === goal.id) {
                setCTAHelpText(true)
            } else {
                setGoal(goal)
                setCTAHelpText(false)
            }
        },
        basisTypeId
    )

    const servicesTypesComponent: ReactFragment = ServiceTypesComponent(
        services,
        selectedService as ServiceBF,
        (service) => {
            if (isMobile && selectedService?.id === service.id) {
                setCTAHelpText(true)
            } else {
                setService(service)
                setCTAHelpText(false)
            }
        },
        basisTypeId
    )

    const klippecardButton: ReactFragment = KlippeCardButton(
        selectedGoal,
        selectedService,
        onClickKlippeKortButtonHeaderDesktop,
        basisTypeId,
        isCompanyBooking
    )

    const goToCalendar: ReactFragment = GoToCalendarComponent(
        selectedGoal,
        selectedService,
        onClickServiceCTAMobile,
        showCTAHelpText
    )

    const goToServicePicker: ReactFragment = GoToServicePicker(
        selectedGoal,
        selectedService,
        onClickGoToServicePicker
    )

    const partnerPickerComponent = () =>
        PartnerPickerComponent(
            selectedService,
            basisType,
            partners,
            selectedPartner,
            setPartner,
            isMobile,
            isCompanyBooking,
            selectedDate
        )

    const servicePicker: ReactFragment = ServicePickerComponent(
        goalsComponent,
        servicesTypesComponent,
        klippecardButton
    )

    const loadPartnerModal = () => {
        const partnerModalProps = {
            activeTab,
            selectedGoal: selectedGoal!,
            selectedPartner: selectedPartner!,
            selectedService: selectedService!,
            selectedDate,
            time,
            onClickBook,
            canClickNext,
            onClickKlippeKortButtonHeaderDesktop,
            partnerModalCarouselRef,
            dates,
            isMobile,
            setDate,
            timeSlots,
            setTime,
            handleOnCloseModal,
            handleOnOpenModal,
            basisType,
            onScrollEvent,
            isCompanyBooking,
        }

        if (
            selectedGoal !== undefined &&
            selectedService !== undefined &&
            selectedPartner !== undefined
        )
            return <PartnerModalComponent {...partnerModalProps} />
        return <></>
    }

    // A mobile and a desktop view (since ServicePicker is different on both)
    return isMobile ? (
        <PageTemplate
            Top={
                <ServicePicker
                    css={{
                        minH: servicePickerMinHeight,
                    }}
                    ref={servicePickerRef}
                >
                    <RenderIf condition={isServiceTypePickerOpen}>
                        <Heading bold primary size={5}>
                            {t(basisType.t.name)}
                        </Heading>
                        {servicePicker}
                        {selectedService && (
                            <ServiceTexts>
                                <Text bold primary>
                                    {t(
                                        `common:servicetypes.${selectedService.id}.info.title`
                                    )}
                                </Text>
                                <Text primary>
                                    {t(
                                        `common:servicetypes.${selectedService.id}.info.text`
                                    )}
                                </Text>
                            </ServiceTexts>
                        )}
                        {goToCalendar}
                    </RenderIf>
                    <RenderIf condition={!isServiceTypePickerOpen}>
                        {goToServicePicker}
                        <ItemWithHeader
                            header={t('calendar.carousel.title')}
                            item={carouselComponent()}
                        />
                    </RenderIf>
                </ServicePicker>
            }
            Body={
                <RenderIf condition={!isServiceTypePickerOpen}>
                    {partnerPickerComponent()}
                </RenderIf>
            }
            Modal={isModalOpen && loadPartnerModal()}
            hideBackButton={!isServiceTypePickerOpen}
        />
    ) : (
        <PageTemplate
            Top={
                <ServicePicker>
                    <Heading bold primary size={4}>
                        {t(basisType.t.name)}
                    </Heading>
                    {servicePicker}
                    <ItemWithHeader
                        header={t('calendar.carousel.title')}
                        item={carouselComponent()}
                    />
                </ServicePicker>
            }
            Body={partnerPickerComponent()}
            Modal={isModalOpen && loadPartnerModal()}
        />
    )
}
